import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaArrowRightArrowLeft } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import ModalLayout from '../layouts/ModalLayout';
import Button from '../buttons/Button';
import DropdownSelect from '../forms/DropdownSelect';
import photoAlbumService from '../../../services/photoAlbumService';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';

interface MovePhotoModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  photo?: Photo | null;
  photoIds?: number[];
  currentAlbumId: string;
  onPhotoMoved: (photoId: number | number[]) => void;
}

export default function MovePhotoModal({
  open,
  setOpen,
  photo,
  photoIds,
  currentAlbumId,
  onPhotoMoved
}: MovePhotoModalProps): JSX.Element {
  const { t } = useTranslation();
  const business = useSelectedBusiness();
  const [albums, setAlbums] = useState<PhotoAlbum[]>([]);
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedAlbumId(undefined);
      setAlbums([]);
    }
  }, [open]);

  useEffect(() => {
    const fetchAlbums = async () => {
      if (!business?.businessId || !open) return;
      
      setIsLoading(true);
      try {
        const allAlbums = await photoAlbumService.getPhotoAlbums(business.businessId);
        const availableAlbums = allAlbums.filter(album => album.id !== currentAlbumId);
        setAlbums(availableAlbums);
      } catch (error) {
        console.error('Failed to fetch albums:', error);
        toast.error(t('toast.error.unexpected'));
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchAlbums();
    }
  }, [business?.businessId, currentAlbumId, open, t]);

  const handleMovePhoto = async () => {
    if (!selectedAlbumId) return;

    // Determine if we're moving a single photo or multiple photos
    const isMultiple = photoIds && photoIds.length > 0;
    const isSingle = photo && !isMultiple;

    if (!isMultiple && !isSingle) return;

    setIsLoading(true);
    try {
      if (isMultiple && photoIds) {
        // Move multiple photos
        await photoAlbumService.movePhotos(currentAlbumId, photoIds, selectedAlbumId);
        onPhotoMoved(photoIds);
        toast.success(t('page.photoAlbum.movePhotos.success', { count: photoIds.length }));
      } else if (isSingle && photo) {
        // Move single photo
        await photoAlbumService.movePhoto(currentAlbumId, photo.id, selectedAlbumId);
        onPhotoMoved(photo.id);
        toast.success(t('page.photoAlbum.movePhoto.success'));
      }
      setOpen(false);
      setSelectedAlbumId(undefined);
    } catch (error) {
      console.error('Error moving photo(s):', error);
      toast.error(t('toast.error.unexpected'));
    } finally {
      setIsLoading(false);
    }
  };

  const albumOptions = albums.map(album => ({
    label: album.title,
    value: album.id
  }));

  // Determine if we're in multiple or single mode
  const isMultiple = photoIds && photoIds.length > 0;
  const isSingle = photo && !isMultiple;

  if (!isMultiple && !isSingle) {
    return <></>;
  }

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      className="relative transform overflow-hidden rounded-[20px] bg-secondary-50 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md"
      closeButton
    >
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <FaArrowRightArrowLeft className="h-6 w-6 text-primary-900" />
            <h3 className="text-lg font-semibold text-primary-900">
              {isMultiple 
                ? t('page.photoAlbum.movePhotos.title')
                : t('page.photoAlbum.movePhoto.title')
              }
            </h3>
          </div>
          <p className="text-sm text-gray-600">
            {isMultiple 
              ? t('page.photoAlbum.movePhotos.description', { count: photoIds?.length })
              : t('page.photoAlbum.movePhoto.description')
            }
          </p>
        </div>

        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-primary-900">
            {isMultiple 
              ? t('page.photoAlbum.movePhotos.selectAlbum')
              : t('page.photoAlbum.movePhoto.selectAlbum')
            }
          </label>
          <DropdownSelect
            options={albumOptions}
            value={selectedAlbumId}
            setValue={setSelectedAlbumId}
            placeholder={isLoading ? t('general.loading') : (isMultiple 
              ? t('page.photoAlbum.movePhotos.selectAlbumPlaceholder')
              : t('page.photoAlbum.movePhoto.selectAlbumPlaceholder')
            )}
            isDisabled={isLoading}
            isLoading={isLoading}
            noOptionsMessage={() => albums.length === 0 && !isLoading ? (isMultiple 
              ? t('page.photoAlbum.movePhotos.noAlbumsAvailable')
              : t('page.photoAlbum.movePhoto.noAlbumsAvailable')
            ) : null}
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              setOpen(false);
              setSelectedAlbumId(undefined);
            }}
            disabled={isLoading}
          >
            {t('general.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleMovePhoto}
            disabled={!selectedAlbumId || isLoading}
            className="flex items-center gap-2"
          >
            <FaArrowRightArrowLeft className="h-4 w-4" />
            {isLoading ? t('general.moving') : (isMultiple 
              ? t('page.photoAlbum.movePhotos.move')
              : t('page.photoAlbum.movePhoto.move')
            )}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}
